import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth, useUi } from '../lib/store.js';
import { useProjects, useApprovals, useProject, useInvalidate, useReadiness, type ProjectSummary } from '../lib/queries.js';
import { useSetupSteps } from '../lib/pipeline.js';
import { openEventStream } from '../lib/api.js';
import { num } from '../lib/format.js';
import { useT, setzeSprache, useSprache } from '../lib/i18n.js';
import { ToastHost, Note } from './ui.js';
import { AssistantPanel } from './AssistantPanel.js';
import { Paywall } from './Paywall.js';
import { DevTools } from './DevTools.js';
import { ProjectAvatar } from './ProjectAvatar.js';
import { PipelineBar } from './PipelineBar.js';
import { ActivityDock } from './ActivityDock.js';

/**
 * Das Gerüst (README §6.0): Seitenleiste links, oben die Kopfzeile, im
 * Projekt darunter die Pipeline-Leiste, rechts die aufklappbare
 * Aktivitätsleiste. Nur der Inhalt, die Seitenleiste und die Aktivität
 * scrollen, jeweils für sich.
 *
 * Es gibt nur noch drei nummerierte Hauptaufgaben: 1 Einrichten, 2 Prüfen,
 * 3 Veröffentlichen. Alles andere liegt unter „Erweitert".
 */

type NavKey = 'overview' | 'setup' | 'content' | 'results' | 'automation' | 'recipes' | 'channels';

interface NavEntry {
  key: NavKey;
  slug: string;
  label: string;
  mark: string;
}

const MAIN_NAV: NavEntry[] = [
  { key: 'overview', slug: '', label: 'Übersicht', mark: '◎' },
  { key: 'setup', slug: 'setup', label: 'Projekt', mark: '1' },
  { key: 'content', slug: 'build', label: 'Posts', mark: '2' },
  { key: 'automation', slug: 'automation', label: 'Automatik', mark: '3' },
  { key: 'results', slug: 'results', label: 'Analysen', mark: '4' },
];

const ADVANCED_NAV: NavEntry[] = [
  { key: 'recipes', slug: 'recipes', label: 'Rezepte', mark: '✦' },
  { key: 'channels', slug: 'channels', label: 'KI & Kanäle', mark: '⇄' },
];

/** Welcher Eintrag zu einer Adresse gehört (auch für tiefere Seiten). */
function activeKey(pathname: string, projectId?: string): NavKey | null {
  if (!projectId) return null;
  const rest = pathname.split(`/projects/${projectId}`)[1] ?? '';
  const first = rest.split('/').filter(Boolean)[0] ?? '';
  switch (first) {
    case '': return 'overview';
    case 'run': case 'build': case 'review': case 'builder': case 'publish': return 'content';
    case 'setup': return 'setup';
    case 'results': return 'results';
    case 'automation': return 'automation';
    case 'recipes': return 'recipes';
    case 'channels': return 'channels';
    default: return 'overview';
  }
}

/** Seitentitel für Brotkrumen und mobilen Kopf. */
function pageTitle(pathname: string, projectId: string | undefined, t: (s: string) => string): string {
  if (projectId) {
    const rest = pathname.split(`/projects/${projectId}`)[1] ?? '';
    const first = rest.split('/').filter(Boolean)[0] ?? '';
    const map: Record<string, string> = {
      '': 'Übersicht', run: 'Lauf', build: 'Beiträge bauen', setup: 'Einrichten', review: 'Prüfen', builder: 'Bearbeiten',
      publish: 'Kalender', results: 'Analysen', automation: 'Automatik', recipes: 'Rezepte', channels: 'KI & Kanäle',
    };
    return t(map[first] ?? 'Übersicht');
  }
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  const map: Record<string, string> = {
    '': 'Alle Projekte', approvals: 'Freigaben', billing: 'Guthaben & Tarif',
    settings: 'Einstellungen', music: 'Musik',
  };
  return t(map[first] ?? 'Alle Projekte');
}

export function projectTypeLabel(type: ProjectSummary['type'] | undefined, t: (s: string) => string): string {
  return type === 'product' ? t('Produkt') : type === 'company' ? t('Unternehmen') : t('App');
}

/** Ein Satz, woran ein Projekt gerade steht, und die passende Farbe. */
export function projectStatus(p: ProjectSummary, t: (s: string, v?: Record<string, string | number>) => string): { text: string; tone: 'accent' | 'warn' | 'ok' | 'err' | 'idle' } {
  if (p.activeRun) return { text: t('Baut gerade · {p} %', { p: p.activeRun.progress }), tone: 'accent' };
  const pending = p.stats?.pendingApproval ?? 0;
  if (pending > 0) return { text: pending === 1 ? t('1 Beitrag wartet auf dich') : t('{n} Beiträge warten auf dich', { n: pending }), tone: 'warn' };
  const expiring = (p.accounts ?? []).find((a) => a.connected && a.tokenHealth !== 'ok');
  if (expiring) return { text: t('{platform}-Zugang läuft ab', { platform: expiring.platform === 'tiktok' ? 'TikTok' : 'Instagram' }), tone: 'warn' };
  if (p.accounts && !p.accounts.some((a) => a.connected)) return { text: t('Kein Kanal verbunden'), tone: 'err' };
  if (p.autopilotHold?.reason) return { text: t('Automatik pausiert'), tone: 'warn' };
  if (p.autopilot?.enabled) return { text: t('Läuft von allein'), tone: 'ok' };
  return { text: t('Automatik ist aus'), tone: 'idle' };
}

const TONE_DOT: Record<string, string> = {
  accent: 'var(--accent)', warn: 'var(--warn)', ok: 'var(--ok)', err: 'var(--err)', idle: 'var(--panel2)',
};

export function Layout() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const { user, org, logout, setCredits } = useAuth();
  const { theme, toggleTheme, tipsOn, setTips, dockOpen, setDock } = useUi();
  const invalidate = useInvalidate();

  const { data: projects } = useProjects();
  const { data: approvals } = useApprovals();
  const { data: project } = useProject(projectId);
  const setup = useSetupSteps(projectId);

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantSeed, setAssistantSeed] = useState<{ message: string; at: number } | null>(null);
  const [userMenu, setUserMenu] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileSwitcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return openEventStream((event) => {
      const type = String(event.type ?? '');
      if (type === 'stream.reconnected') {
        invalidate.posts();
        invalidate.approvals();
        invalidate.projects();
      }
      if (type === 'credits.update') {
        setCredits(Number(event.balance ?? 0), event.reserved == null ? undefined : Number(event.reserved));
        invalidate.billing();
      }
      if (type === 'post.update') {
        invalidate.posts();
        invalidate.approvals();
        if (event.postId) invalidate.post(String(event.postId));
        // Zähler an der Seitenleiste und in der Pipeline hängen am Projekt.
        if (event.projectId) invalidate.project(String(event.projectId));
      }
      if (type === 'agent_run.update' || type === 'agent_run.log') {
        if (event.projectId) invalidate.run(String(event.projectId));
        invalidate.runDetail();
        invalidate.projects();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Andere Seiten (z. B. „Lieber einrichten lassen?") öffnen den Assistenten
  // über ein Fenster-Ereignis, ohne dass der Zustand durch alle Ebenen muss.
  // Mit `detail.message` öffnet er gleich mit dieser Bitte, z. B. „Richte mit mir ein Projekt ein".
  useEffect(() => {
    const open = (e: Event) => {
      setAssistantOpen(true);
      const message = (e as CustomEvent<{ message?: string } | undefined>).detail?.message;
      if (message) setAssistantSeed({ message, at: Date.now() });
    };
    window.addEventListener('aff:assistant', open);
    return () => window.removeEventListener('aff:assistant', open);
  }, []);

  // Seiten außerhalb des Rahmens (Anlege-Dialog) kommen über ?assistant=… hierher.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ask = params.get('assistant');
    if (!ask) return;
    setAssistantOpen(true);
    if (ask === 'setup') setAssistantSeed({ message: t('Richte mit mir ein neues Projekt ein'), at: Date.now() });
    params.delete('assistant');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSwitcherOpen(false);
    setUserMenu(false);
    // Nicht das Fenster scrollt, sondern der Inhaltsbereich.
    document.querySelector('.page')?.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Menüs schließen sich beim Klick daneben und mit Escape.
  useEffect(() => {
    if (!switcherOpen && !userMenu) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (switcherOpen && !switcherRef.current?.contains(target) && !mobileSwitcherRef.current?.contains(target)) setSwitcherOpen(false);
      if (userMenu && !userMenuRef.current?.contains(target)) setUserMenu(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setSwitcherOpen(false); setUserMenu(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [switcherOpen, userMenu]);

  const aktuelleSprache = useSprache();
  const current = activeKey(location.pathname, projectId);
  const title = pageTitle(location.pathname, projectId, t);
  const pending = projectId ? project?.stats?.pendingApproval ?? 0 : approvals?.total ?? 0;
  const channelsMissing = !!project && (
    !(project.accounts ?? []).some((a) => a.connected)
    || (project.accounts ?? []).some((a) => a.connected && a.tokenHealth !== 'ok')
  );
  const working = (projects ?? []).filter((p) => p.activeRun);
  const workingPct = working.length
    ? Math.round(working.reduce((a, p) => a + (p.activeRun?.progress ?? 0), 0) / working.length)
    : null;
  const available = org?.available ?? 0;
  const reserved = org?.creditsReserved ?? 0;
  /*
   * Freundeskonten bringen eigene Anbieter-Keys mit. Solange keiner drin
   * steht, baut die Anwendung NICHTS -- und das erfuhr man bisher erst,
   * wenn ein Lauf scheiterte, oder auf einer Seite, zu der kein Weg führte.
   * Der Hinweis steht deshalb auf jeder Seite, bis Texte und Bilder
   * abgedeckt sind. Gefragt wird nur für diese Konten.
   */
  const { data: keyReadiness } = useReadiness(user?.ownKeysRequired ? projectId : undefined, !!user?.ownKeysRequired);
  const keysMissingAnywhere = !!user?.ownKeysRequired && !!keyReadiness && !keyReadiness.ok;
  const keysMissing = keysMissingAnywhere
    && !location.pathname.startsWith('/settings') && !location.pathname.endsWith('/channels');
  const lowCredits = !user?.unlimited && available < 400;
  const showCredits = !user?.unlimited || !!user?.operator;
  const herkunft = { state: { from: location.pathname, ...(project?.name ? { fromLabel: project.name } : {}) } };

  const href = (slug: string) => `/projects/${projectId}${slug ? `/${slug}` : ''}`;
  const badgeFor = (key: NavKey): ReactNode => {
    if (key === 'setup' && setup.ready && setup.done < setup.total) {
      return <span className={`nav-badge ${setup.next ? 'nav-badge-warn' : ''}`}>{setup.done}/{setup.total}</span>;
    }
    if (key === 'content' && pending > 0) return <span className="nav-badge nav-badge-warn">{pending}</span>;
    if (key === 'channels' && channelsMissing) return <span className="nav-badge nav-badge-warn">!</span>;
    return null;
  };

  /** Beim Projektwechsel auf derselben Unterseite bleiben. */
  const switchTo = (id: string) => {
    const rest = projectId ? location.pathname.split(`/projects/${projectId}`)[1] ?? '' : '';
    const first = rest.split('/').filter(Boolean)[0] ?? '';
    const keep = ['setup', 'build', 'review', 'publish', 'automation', 'results', 'recipes', 'channels'].includes(first) ? `/${first}` : '';
    navigate(`/projects/${id}${keep}`);
    setSwitcherOpen(false);
  };

  const switcherMenu = (
    <div className="menu" style={{ top: 'calc(100% + 6px)', left: 0, width: 300 }} role="menu">
      <div className="menu-label">{t('Projekt wechseln')}</div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {(projects ?? []).map((p) => {
          const st = projectStatus(p, t);
          return (
            <button
              key={p.id}
              role="menuitem"
              className={`menu-item ${p.id === projectId ? 'menu-item-on' : ''}`}
              onClick={() => switchTo(p.id)}
            >
              <ProjectAvatar name={p.name} accentColor={p.accentColor} coverUrl={p.coverUrl} size={28} radius={8} />
              <span className="grow col" style={{ minWidth: 0 }}>
                <span className="truncate" style={{ color: 'var(--text)', fontWeight: 600 }}>{p.name}</span>
                <span className="truncate dim" style={{ fontSize: 12 }}>{st.text}</span>
              </span>
              <span className="dot" style={{ background: TONE_DOT[st.tone] }} />
            </button>
          );
        })}
      </div>
      <div className="menu-sep" />
      <button className="menu-item" style={{ color: 'var(--accent-text)' }} onClick={() => navigate('/new')}>
        <span className="nav-icon">+</span>
        <span>{t('Neues Projekt')}</span>
      </button>
      <button className="menu-item" onClick={() => navigate('/')}>
        <span className="nav-icon">▦</span>
        <span>{t('Alle Projekte ansehen')}</span>
      </button>
    </div>
  );

  const userMenuEl = userMenu && (
    <div className="menu" style={{ left: 0, bottom: 'calc(100% + 6px)', width: 240 }} role="menu">
      <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid var(--line)', marginBottom: 5 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }} className="truncate">{user?.name}</div>
        <div className="dim truncate" style={{ fontSize: 12 }}>{user?.email}</div>
        {!user?.unlimited && (
          <span className="pill pill-info" style={{ marginTop: 8 }}>
            {t('Tarif {plan}', { plan: org?.planName ?? org?.plan ?? '' })}
          </span>
        )}
      </div>
      {showCredits && (
        <button className="menu-item" onClick={() => navigate('/billing', herkunft)}>
          <span className="nav-icon">◈</span> {t('Guthaben & Tarif')}
        </button>
      )}
      <button className="menu-item" onClick={() => navigate('/approvals', herkunft)}>
        <span className="nav-icon">✓</span> {t('Alles prüfen (alle Projekte)')}
      </button>
      <button className="menu-item" onClick={() => navigate('/music', herkunft)}>
        <span className="nav-icon">♪</span> {t('Musik')}
      </button>
      <button className="menu-item" onClick={() => navigate('/settings', herkunft)}>
        <span className="nav-icon">⚙</span> {t('Einstellungen')}
      </button>
      {/* Sprache steht im Kontomenü: Wer die Anwendung in der falschen
          Sprache vor sich hat, findet den Weg zu einer Unterseite nicht. */}
      <div className="row" style={{ gap: 4, padding: '6px 10px' }}>
        {(['de', 'en'] as const).map((code) => (
          <button
            key={code}
            type="button"
            className={`chip ${aktuelleSprache === code ? 'chip-on' : ''}`}
            style={{ padding: '3px 10px', minHeight: 0 }}
            onClick={() => setzeSprache(code)}
          >
            {code === 'de' ? 'Deutsch' : 'English'}
          </button>
        ))}
      </div>
      <div className="menu-sep" />
      <button className="menu-item" style={{ color: 'var(--err)' }} onClick={() => { void logout(); }}>
        <span className="nav-icon">⏻</span> {t('Abmelden')}
      </button>
    </div>
  );

  const themeButton = (size?: number) => (
    <button
      type="button"
      className="icon-btn"
      onClick={toggleTheme}
      aria-label={theme === 'dark' ? t('Helles Design') : t('Dunkles Design')}
      title={theme === 'dark' ? t('Helles Design') : t('Dunkles Design')}
      style={size ? { width: size, height: size, borderRadius: '50%' } : undefined}
    >
      {theme === 'dark' ? '☀' : '☾'}
    </button>
  );

  return (
    <div className="shell">
      {/* ═══════════════════════════════════════════ Seitenleiste */}
      <aside className="sidebar" aria-label={t('Navigation')}>
        <button className="side-brand" onClick={() => navigate('/')} title={t('Alle Projekte')}>
          <img src="/logo-mark.webp" alt="" />
          <span>AI Feed Factory</span>
        </button>

        {projectId && (
          <div ref={switcherRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={`switcher ${switcherOpen ? 'switcher-open' : ''}`}
              onClick={() => setSwitcherOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={switcherOpen}
            >
              {project ? (
                <ProjectAvatar name={project.name} accentColor={project.accentColor} coverUrl={project.coverUrl} size={34} radius={10} />
              ) : (
                <span className="skeleton" style={{ width: 34, height: 34, borderRadius: 10 }} />
              )}
              <span className="grow col" style={{ minWidth: 0 }}>
                <span className="switcher-kicker">{t('Projekt')} · {projectTypeLabel(project?.type, t)}</span>
                <span className="switcher-name truncate">{project?.name ?? '…'}</span>
              </span>
              <span className="switcher-caret">▾</span>
            </button>
            {switcherOpen && switcherMenu}
          </div>
        )}

        {projectId ? (
          <>
            <div className="nav-group">
              <div className="nav-group-label">{t('Dein Feed')}</div>
              {MAIN_NAV.map((n) => (
                <NavLink
                  key={n.key}
                  to={href(n.slug)}
                  end={!n.slug}
                  className={`nav-link ${current === n.key ? 'nav-link-active' : ''}`}
                >
                  <span className="nav-mark">{n.mark}</span>
                  <span className="grow">{t(n.label)}</span>
                  {badgeFor(n.key)}
                </NavLink>
              ))}
            </div>
            <details className="nav-group factory-advanced" open={current === 'recipes' || current === 'channels' || undefined}>
              <summary>{t('Weitere Einstellungen')}</summary>
              {ADVANCED_NAV.map((n) => (
                <NavLink
                  key={n.key}
                  to={href(n.slug)}
                  className={`nav-link ${current === n.key ? 'nav-link-active' : ''}`}
                >
                  <span className="nav-mark">{n.mark}</span>
                  <span className="grow">{t(n.label)}</span>
                  {badgeFor(n.key)}
                </NavLink>
              ))}
            </details>
          </>
        ) : null}

        <div className={`nav-group ${projectId ? 'nav-group-account' : ''}`}>
          <div className="nav-group-label">{t('Konto')}</div>
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
            <span className="nav-mark">▦</span>
            <span className="grow">{t('Alle Projekte')}</span>
            {!projectId && (approvals?.total ?? 0) > 0 && <span className="nav-badge nav-badge-warn">{approvals?.total}</span>}
          </NavLink>
          {showCredits && (
            <NavLink to="/billing" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
              <span className="nav-mark">◈</span>
              <span className="grow">{t('Guthaben & Tarif')}</span>
            </NavLink>
          )}
        </div>

        {/*
          Drei Fassungen der Karte, je nach Konto:
            Freundeskonto  -- zahlt keine Credits, braucht eigene Keys
            leeres Konto   -- 0 Credits, aber der Probelauf kostet nichts
            alle anderen   -- Stand, Reservierung, Aufladen
          Vorher hatte ein Freundeskonto hier gar nichts und ein neues Konto
          ein rotes „fast leer", bevor es überhaupt etwas ausgegeben hatte.
        */}
        {user?.ownKeysRequired ? (
          <div className="credit-card">
            <div className="row">
              <span className="credit-card-num grow">{t('Eigene Keys')}</span>
              <span className="pill pill-ok">{t('Befreit')}</span>
            </div>
            <div className="dim" style={{ fontSize: 12 }}>
              {keysMissingAnywhere ? t('Noch kein Key hinterlegt.') : t('Kosten laufen direkt über deine Anbieter.')}
            </div>
            <button type="button" className="btn-link" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/settings#generatoren', herkunft)}>
              {t('Keys verwalten')}
            </button>
          </div>
        ) : showCredits && !user?.unlimited && available <= 0 && reserved <= 0 ? (
          <div className="credit-card">
            <div className="row">
              <span className="credit-card-num grow">{t('0 Credits')}</span>
              <button type="button" className="btn-link" onClick={() => navigate('/billing#packs', herkunft)}>{t('Aufladen')}</button>
            </div>
            <div className="dim" style={{ fontSize: 12 }}>{t('Der Probelauf ist kostenlos.')}</div>
          </div>
        ) : showCredits ? (
          <div className={`credit-card ${lowCredits ? 'credit-card-low' : ''}`}>
            <div className="row">
              <span className="credit-card-num grow">
                {user?.unlimited ? '∞' : num(available)} {t('Credits')}
              </span>
              <button type="button" className="btn-link" onClick={() => navigate('/billing#packs', herkunft)}>{t('Aufladen')}</button>
            </div>
            <div className="bar">
              <div
                className="bar-fill"
                style={{ width: `${user?.unlimited ? 100 : Math.min(100, Math.round((available / Math.max(1, available + reserved)) * 100))}%` }}
              />
            </div>
            <div className="dim" style={{ fontSize: 12 }}>
              {lowCredits
                ? t('fast leer')
                : reserved > 0
                  ? t('{n} vorab reserviert, Unverbrauchtes kommt zurück', { n: num(reserved) })
                  : t('Nichts reserviert')}
            </div>
          </div>
        ) : null}

        <div className="side-user" style={showCredits || user?.ownKeysRequired ? undefined : { marginTop: 'auto' }}>
          <div ref={userMenuRef} style={{ position: 'relative' }} className="grow">
            <button
              type="button"
              className="row"
              onClick={() => setUserMenu((v) => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', gap: 10, padding: 0, width: '100%', textAlign: 'left' }}
              aria-haspopup="menu"
              aria-expanded={userMenu}
            >
              <span className="avatar-round">{(user?.name ?? '?').slice(0, 2).toUpperCase()}</span>
              <span className="truncate" style={{ fontSize: 13.5, fontWeight: 500 }}>{user?.name}</span>
            </button>
            {userMenuEl}
          </div>
          {themeButton()}
        </div>
      </aside>

      {/* ═══════════════════════════════════════════ Hauptbereich */}
      <div className="shell-main">
        <header className="topbar">
          <div className="crumbs grow truncate">
            {projectId ? <><NavLink to="/">{t('Startseite')}</NavLink><span aria-hidden="true"> / </span><NavLink to={href('')}>{project?.name ?? '…'}</NavLink><span aria-hidden="true"> / </span><b>{title}</b></> : <b>{title}</b>}
          </div>
          <button
            type="button"
            className={`top-pill ${tipsOn ? 'top-pill-on' : ''}`}
            onClick={() => setTips(!tipsOn)}
            aria-pressed={tipsOn}
          >
            <span aria-hidden="true">✧</span> {t('Tipps')}
          </button>
          <button type="button" className="top-pill" onClick={() => setAssistantOpen(true)}>
            ✦ {t('Assistent fragen')}
          </button>
          <button
            type="button"
            className={`top-pill ${dockOpen ? 'top-pill-active' : ''}`}
            onClick={() => setDock(!dockOpen)}
            aria-pressed={dockOpen}
          >
            <span className={workingPct !== null ? 'live-dot' : 'dot dot-idle'} />
            {t('Aktivität')}{workingPct !== null ? ` ${workingPct} %` : ''}
          </button>
        </header>

        {/* ── Mobil: Kopf statt Seitenleiste ── */}
        <header className="mobile-header">
          <div ref={mobileSwitcherRef} style={{ position: 'relative', minWidth: 0 }} className="grow">
            <button
              type="button"
              className="row"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', gap: 10, textAlign: 'left', minWidth: 0, width: '100%' }}
              onClick={() => (projectId ? setSwitcherOpen((v) => !v) : navigate('/'))}
            >
              {project ? (
                <ProjectAvatar name={project.name} accentColor={project.accentColor} coverUrl={project.coverUrl} size={34} radius={10} />
              ) : (
                <img src="/logo-mark.webp" alt="" style={{ width: 34, height: 34, borderRadius: 10 }} />
              )}
              <span className="col" style={{ minWidth: 0 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>{title}</span>
                <span className="truncate" style={{ fontSize: 16, fontWeight: 600 }}>
                  {project?.name ?? 'AI Feed Factory'} {projectId ? '▾' : ''}
                </span>
              </span>
            </button>
            {switcherOpen && projectId && switcherMenu}
          </div>
          {themeButton(40)}
          {showCredits && (
            <button type="button" className="top-pill" onClick={() => navigate('/billing', herkunft)} style={lowCredits ? { color: 'var(--err)' } : undefined}>
              {user?.unlimited ? '∞' : num(available)}
            </button>
          )}
          <button type="button" className="avatar-round" onClick={() => navigate('/settings', herkunft)} aria-label={t('Konto')}>
            {(user?.name ?? '?').slice(0, 2).toUpperCase()}
          </button>
        </header>

        {projectId && <details className="factory-pipeline" open={workingPct !== null || undefined}><summary>{t('Ablauf und aktueller Status')}</summary><PipelineBar projectId={projectId} /></details>}

        <main className="page">
          <div className="page-inner">
            {keysMissing && (
              <div style={{ marginBottom: 18 }}>
                <Note
                  tone="warn"
                  title={t('Zuerst deine Anbieter-Keys eintragen')}
                  action={
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/settings#generatoren', herkunft)}>
                      {t('Keys eintragen')}
                    </button>
                  }
                >
                  {t('Dein Konto ist ein Testkonto ohne Credits. Dafür laufen Texte und Bilder über deine eigenen Zugänge bei den Anbietern. Ein einziger Google-Key (Gemini) reicht für Planung, Bilder und Video.')}
                </Note>
              </div>
            )}
            <Outlet />

            {/*
              Rechtliches, von jeder Seite aus erreichbar. Der
              Kündigungsbutton muss ohne Suche zu finden sein und darf keine
              Anmeldung verlangen (§ 312k BGB).
            */}
            <footer className="legal-foot">
              <a href="/impressum" target="_blank" rel="noreferrer">{t('Impressum')}</a>
              <a href="/terms" target="_blank" rel="noreferrer">{t('Nutzungsbedingungen')}</a>
              <a href="/privacy" target="_blank" rel="noreferrer">{t('Datenschutz')}</a>
              <a href="/widerruf" target="_blank" rel="noreferrer">{t('Widerruf')}</a>
              <a href="/kuendigen" target="_blank" rel="noreferrer">{t('Verträge kündigen')}</a>
              <span className="grow" />
              <span className="dim">AI Feed Factory</span>
            </footer>
          </div>
        </main>

        {/* ── Mobil: Tab-Leiste unten ── */}
        {projectId && (
          <nav className="mobile-tabs" aria-label={t('Projekt-Navigation')}>
            {[
              { key: 'setup', slug: 'setup', glyph: '1', label: t('Projekt'), badge: setup.next ? 1 : 0 },
              { key: 'content', slug: 'build', glyph: '2', label: t('Posts'), badge: pending },
              { key: 'automation', slug: 'automation', glyph: '3', label: t('Automatik'), badge: 0 },
              { key: 'results', slug: 'results', glyph: '4', label: t('Analysen'), badge: 0 },
            ].map((tab) => (
              <NavLink
                key={tab.key}
                to={href(tab.slug)}
                end={!tab.slug}
                className={`mobile-tab ${current === tab.key ? 'mobile-tab-on' : ''}`}
              >
                <span className="mobile-tab-glyph">{tab.glyph}</span>
                <span>{tab.label}</span>
                {tab.badge ? <span className="nav-badge nav-badge-warn">{tab.badge}</span> : null}
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      {dockOpen && <ActivityDock onClose={() => setDock(false)} />}

      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        projectId={projectId ?? null}
        projectName={project?.name}
        seed={assistantSeed}
      />
      <Paywall />
      <DevTools />
      <ToastHost />
    </div>
  );
}
